import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileSignature, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/* -----------------------------------------------------------------
 * Body-HTML helpers
 *
 * The single source of truth for "is this source cited inline?" is
 * the article's `body_html`. The LLM writes a `sources_used` field
 * during generation, but it self-reports — occasionally it lists a
 * URL there while forgetting to actually emit the `<a href>` in the
 * prose, producing a false "cited inline" badge. We recompute the
 * status by scanning body_html for `<a href="…">` occurrences of
 * each URL so the badge tracks reality.
 * ----------------------------------------------------------------- */

// Escape a URL for safe use inside a RegExp. Source URLs can contain
// query strings, dots, plus signs — all of which are regex metachars.
function escapeRegex(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Return true iff `body_html` contains an <a href="url"> to the given
// URL. We match against `href="url"` and `href='url'` and href=url
// unquoted, all case-insensitively.
export function isCitedInline(bodyHtml, url) {
  if (!bodyHtml || !url) return false;
  const re = new RegExp(
    `<a[^>]*\\bhref\\s*=\\s*(["']?)${escapeRegex(url)}\\1`,
    "i",
  );
  return re.test(bodyHtml);
}

// Append a short "See more via <a>Outlet</a>." linking sentence before
// the last </p> in body_html. Mirrors the internal-link insertion
// pattern the app already uses — no LLM round-trip, deterministic,
// works offline. If body_html is empty we synthesize a single <p>.
function insertSourceLink(bodyHtml, { url, outlet, title }) {
  if (!url) return bodyHtml;
  if (isCitedInline(bodyHtml, url)) return bodyHtml; // avoid duplicates
  const label = (outlet || title || url).trim();
  const anchor = `<a href="${url}">${label}</a>`;
  if (!bodyHtml) return `<p><em>See more via ${anchor}.</em></p>`;
  const lastClose = bodyHtml.lastIndexOf("</p>");
  const injection = ` <em>See more via ${anchor}.</em>`;
  if (lastClose === -1) return `${bodyHtml}\n<p>${injection.trim()}</p>`;
  return bodyHtml.slice(0, lastClose) + injection + bodyHtml.slice(lastClose);
}

// Unwrap every <a href="url">inner</a> occurrence, keeping the anchor
// TEXT (so prose stays readable) and dropping the link. We regex on the
// full tag rather than parsing the DOM to preserve whitespace and to
// avoid re-serialising the body (which would flatten formatting).
function removeSourceLink(bodyHtml, url) {
  if (!bodyHtml || !url) return bodyHtml;
  const re = new RegExp(
    `<a\\b[^>]*\\bhref\\s*=\\s*(["']?)${escapeRegex(url)}\\1[^>]*>([\\s\\S]*?)</a>`,
    "gi",
  );
  return bodyHtml.replace(re, "$2");
}

function formatError(detail) {
  if (!detail) return tr("Something went wrong. Please try again.");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function SourcesPanel({ article }) {
  const qc = useQueryClient();
  const [busyUrl, setBusyUrl] = useState(null);

  // Build the display list: prefer the richer `input_sources` (with
  // outlet + title), fall back to the legacy `sources_used` string list.
  const items = useMemo(() => {
    if (article.input_sources && article.input_sources.length > 0)
      return article.input_sources;
    return (article.sources_used || []).map((u) => ({
      url: u,
      outlet: "",
      title: "",
    }));
  }, [article.input_sources, article.sources_used]);

  const bodyHtml = article.body_html || "";

  const mutate = useMutation({
    mutationFn: (patch) => api.updateArticle(article.id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      qc.invalidateQueries({ queryKey: ["articles"] });
      setBusyUrl(null);
    },
    onError: (e) => {
      toast.error(formatError(e?.response?.data?.detail) || e.message);
      setBusyUrl(null);
    },
  });

  const onInsert = (s) => {
    setBusyUrl(s.url);
    const newBody = insertSourceLink(bodyHtml, s);
    if (newBody === bodyHtml) {
      toast.info(tr("This source is already cited inline."));
      setBusyUrl(null);
      return;
    }
    mutate.mutate(
      { body_html: newBody },
      {
        onSuccess: () => {
          toast.success(
            tr("Linked {outlet} in the article.", { outlet: s.outlet || s.title || tr("source") }),
          );
        },
      },
    );
  };

  const onRemove = (s) => {
    setBusyUrl(s.url);
    const newBody = removeSourceLink(bodyHtml, s.url);
    if (newBody === bodyHtml) {
      toast.info(tr("No inline link to remove for this source."));
      setBusyUrl(null);
      return;
    }
    mutate.mutate(
      { body_html: newBody },
      {
        onSuccess: () => {
          toast.success(
            tr("Removed inline link to {outlet}.", { outlet: s.outlet || s.title || tr("source") }),
          );
        },
      },
    );
  };

  const hasNote = /data-newsdesk-source-note\s*=\s*["']?1/i.test(bodyHtml);

  const sourceNoteMutate = useMutation({
    mutationFn: (action) => api.articleSourceNote(article.id, action),
    onSuccess: (_data, action) => {
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      qc.invalidateQueries({ queryKey: ["articles"] });
      toast.success(
        action === "remove"
          ? tr("Source note removed.")
          : tr("Source note added to the article."),
      );
    },
    onError: (e) => {
      toast.error(formatError(e?.response?.data?.detail) || e.message);
    },
  });

  if (items.length === 0) return null;

  return (
    <div className="pt-2" data-testid="sources-panel">
      <ul className="space-y-2 text-xs">
        {items.map((s, i) => {
          const cited = isCitedInline(bodyHtml, s.url);
          const busy = busyUrl === s.url && mutate.isPending;
          return (
            <li
              key={`${s.url}-${i}`}
              data-testid={`source-row-${i}`}
              className="flex items-start gap-2 flex-wrap"
            >
              <div className="min-w-0 flex-1">
                {s.outlet && (
                  <div className="font-serif-display text-sm text-[var(--ink)]">
                    {s.outlet}
                    {cited && (
                      <span
                        data-testid={`source-cited-badge-${i}`}
                        className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-mono-display align-middle"
                      >{tr("cited inline")}</span>
                    )}
                  </div>
                )}
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--accent)] hover:text-[var(--accent-hover)] break-all underline underline-offset-2"
                >
                  {s.title || s.url}
                </a>
                {!s.outlet && cited && (
                  <span
                    data-testid={`source-cited-badge-${i}`}
                    className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-mono-display"
                  >{tr("cited inline")}</span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--ink-muted)] hover:text-[var(--ink)] p-1"
                  title={tr("Open source")}
                >
                  <ExternalLink className="w-3 h-3" />
                </a>
                {cited ? (
                  <button
                    data-testid={`source-remove-${i}`}
                    onClick={() => onRemove(s)}
                    disabled={busy}
                    title={tr("Remove the inline hyperlink to this source")}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border border-[var(--border)] hover:border-red-500 hover:text-red-700 disabled:opacity-50"
                  >
                    {busy ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <X className="w-3 h-3" />
                    )}{tr("Remove link")}</button>
                ) : (
                  <button
                    data-testid={`source-insert-${i}`}
                    onClick={() => onInsert(s)}
                    disabled={busy}
                    title={tr("Insert an inline hyperlink to this source in the article")}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border border-[var(--border)] hover:border-[var(--ink)] hover:bg-white disabled:opacity-50"
                  >
                    {busy ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Plus className="w-3 h-3" />
                    )}{tr("Insert")}</button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-[11px] text-[var(--ink-muted)]">{trRich("“Cited inline” means the article body contains a direct <0><a href></0> link to that source. Click <1>Insert</1> to weave a short “See more via…” sentence into the last paragraph, or <2>Remove link</2> to unwrap an existing one while keeping the surrounding text.", null, [<code className="font-mono-display" />, <strong />, <strong />])}</p>
      <div className="mt-3 pt-3 border-t border-[var(--border)]">
        <button
          type="button"
          data-testid="source-note-toggle"
          onClick={() =>
            sourceNoteMutate.mutate(hasNote ? "remove" : "add")
          }
          disabled={sourceNoteMutate.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-white disabled:opacity-50"
        >
          {sourceNoteMutate.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <FileSignature className="w-3.5 h-3.5" />
          )}
          {hasNote ? tr("Remove source note") : tr("Add source note")}
        </button>
        <p className="mt-2 text-[11px] text-[var(--ink-muted)]">{trRich("Appends an attribution paragraph at the end of the article. Edit the wording in <0>Settings</0> → Source note.", null, [<a href="/settings" className="text-[var(--accent)] underline" />])}</p>
      </div>
    </div>
  );
}
